import { useEffect } from 'react';
import EditorCanvas from './components/Canvas/EditorCanvas';
import Toolbar from './components/Toolbar/Toolbar';
import LayersPanel from './components/Panels/LayersPanel';
import PropertiesPanel from './components/Panels/PropertiesPanel';
import PalettePanel from './components/Panels/PalettePanel';
import SpriteInspectorPanel from './components/Panels/SpriteInspectorPanel';
import AllEntitiesView from './components/Views/AllEntitiesView';
import { useEditorStore } from './stores/editorStore';

function App() {
  const { levelId, levelData, loadLevel, mode, entityPaletteScope, isLoading, error } =
    useEditorStore();
  const isAllEntitiesView = mode === 'entities' && entityPaletteScope === 'all';

  useEffect(() => {
    // Keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Z: Undo
      if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        useEditorStore.getState().undo();
      }
      // Ctrl+Shift+Z: Redo
      if (e.ctrlKey && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        useEditorStore.getState().redo();
      }
      // Ctrl+S: Save
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        useEditorStore.getState().save();
      }
      // Delete/Backspace: Delete selected
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          useEditorStore.getState().deleteSelected();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="editor-container">
      <Toolbar />
      {isAllEntitiesView ? (
        <div className="editor-main">
          <div className="editor-canvas-wrapper">
            <AllEntitiesView />
          </div>
        </div>
      ) : (
        <div className="editor-main">
          <div className="editor-sidebar left">
            <LayersPanel />
            <PalettePanel />
          </div>
          <div className="editor-canvas-wrapper">
            {levelData ? (
              <EditorCanvas key={levelId ?? levelData.id} />
            ) : (
              <div className="editor-empty">
                <h2>Imbasion Level Editor</h2>
                <p>{isLoading ? 'Loading level...' : 'Select a level to begin editing'}</p>
                {error && (
                  <p style={{ color: '#ff6b6b', maxWidth: 420, textAlign: 'center' }}>{error}</p>
                )}
                <div className="level-buttons">
                  {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                    <button
                      key={num}
                      onClick={() => loadLevel(`level${num}`)}
                      className="level-button"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Loading...' : `Level ${num}`}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="editor-sidebar right">
            <PropertiesPanel />
          </div>
        </div>
      )}
      {!isAllEntitiesView && <SpriteInspectorPanel />}
      <div className="editor-statusbar">
        <span>Mode: {isAllEntitiesView ? 'all entities' : mode}</span>
        {isLoading && <span>Loading level...</span>}
        {error && <span style={{ color: '#ff6b6b' }}>Error: {error}</span>}
        {levelData && (
          <>
            <span>Level: {levelData.name}</span>
            <span>
              Size: {levelData.width}x{levelData.height}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default App;
